import { StoreStockData } from 'common/components/stocks-in-stores/types';
import log from 'loglevel';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SET_YANDEX_SPLIT_SESSION, RESET_YANDEX_SPLIT_SESSION } from '../reducer';
import { getYandexSplitSession } from '../selectors';

type Args = {
  availablePaymentMethods: string[];
  currency: string;
  env: string;
  hideWidgetHeader: boolean;
  isBasketLoading: boolean;
  merchantId: string;
  price?: number | null;
  quantity: number;
  stocks: StoreStockData[];
  version: number;
  borderRadius: number;
  withOutline: boolean;
};

export const useYandexPayWidget = ({
  availablePaymentMethods,
  currency,
  env,
  borderRadius,
  hideWidgetHeader,
  isBasketLoading,
  merchantId,
  price,
  quantity,
  stocks,
  version,
  withOutline,
}: Args) => {
  const dispatch = useDispatch();
  const widgetRef = useRef<HTMLDivElement>(null);
  const currentSession = useSelector(getYandexSplitSession);
  quantity = quantity || 1;

  useEffect(() => {
    if (currentSession || !stocks.length || !merchantId || !price || isBasketLoading) return;
    const totalAmount = (price * quantity).toFixed(2);

    async function init() {
      const YaPay = window.YaPay;

      if (!YaPay || !widgetRef.current) return;

      try {
        const session = await YaPay.createSession(
          {
            env: YaPay.PaymentEnv[env],
            version,
            currencyCode: currency,
            merchantId,
            totalAmount,
            availablePaymentMethods,
          },
          { onPayButtonClick: () => {}, onFormOpenError: (error) => log.error(error) },
        );

        widgetRef.current.innerHTML = '';
        session.mountWidget(widgetRef.current, {
          widgetType: YaPay.WidgetType.Ultimate,
          widgetTheme: YaPay.WidgetTheme.Light,
          borderRadius,
          padding: YaPay.WidgetPaddingType.Default,
          withOutline,
          widgetBackground: YaPay.WidgetBackgroundType.Default,
          hideWidgetHeader: hideWidgetHeader,
          widgetSize: YaPay.WidgetSize.Medium,
        });

        dispatch(SET_YANDEX_SPLIT_SESSION(session));
      } catch (error) {
        dispatch(RESET_YANDEX_SPLIT_SESSION());
      }
    }

    init();
  }, [
    availablePaymentMethods,
    borderRadius,
    currency,
    currentSession,
    dispatch,
    env,
    hideWidgetHeader,
    isBasketLoading,
    merchantId,
    price,
    quantity,
    stocks.length,
    version,
    widgetRef,
    withOutline,
  ]);

  return widgetRef;
};
